import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { styled } from 'styletron-react';
import { Button, SIZE } from 'baseui/button';
import { HeadingMedium, HeadingSmall, LabelLarge } from 'baseui/typography';
import { Spinner } from 'baseui/spinner';
import { toaster } from 'baseui/toast';
import { Avatar } from 'baseui/avatar';
import { Input } from 'baseui/input';
import { Select } from 'baseui/select';
import { DatePicker } from 'baseui/datepicker';
import { PhoneInput } from 'baseui/phone-input';
import { Checkbox } from 'baseui/checkbox';
import { useQueryClient } from '@tanstack/react-query';
import { defaultInstance } from '../api/httpClient';

import { Content } from './LayoutPage';
import { getGetUserIdQueryKey, useGetUserId, usePatchUserId } from '../api/generated';
import { VerifiedDocumentCard } from '../components/VerifiedDocumentCard';
import type { User } from '../api/generated';
import { S3Image } from '../components/S3Image';
import { MantineDataTable } from '../components/MantineDataTable';
import { type MRT_ColumnDef } from 'mantine-react-table';
import { UserStatusSelector } from '../components/UserStatusSelector';
import { NationalityPicker } from '../components/NationalityPicker';
import { formatNationality, useNationalities } from '../hooks/useNationalities';
import { generateProfilePicture, reAnalyzeFromS3, analyzeDocument } from '../services/documentAnalysis';
import { DocumentScanner } from '../components/DocumentScanner';
import { UploadArea } from '../components/UploadArea';
import { UserX, Upload, Plus } from 'lucide-react';
import { AddUserEstateDrawer } from '../components/AddUserEstateDrawer';
import { AppUserInfoTab } from '../components/AppUserInfoTab';
import { UserEstateTab } from '../components/UserEstateTab';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { Drawer } from 'baseui/drawer';
import { ConfirmationView } from './invitation/components/ConfirmationView';
import { InvitationProvider } from './invitation/context/InvitationContext';
const DrawerComponent = Drawer as any;
import { useMemo } from 'react';
import { useT } from '../i18n/admin';
import { useFdCan } from '../hooks/useFdCan';
import { FrontDeskUserBasics } from '../components/FrontDeskUserBasics';

// SAM-973: the whole user detail uses the full width of the page (no max-width cap).
const Container = styled('div', {
  width: '100%',
  boxSizing: 'border-box',
  padding: '0 16px',
  backgroundColor: '#fff',
  minHeight: '100vh',
  '@media screen and (min-width: 768px)': {
    padding: '0 32px',
  },
});

// Identity document on the left, the data on the right; one column on a phone.
const BasicGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  gap: '24px',
  alignItems: 'start',
  '@media screen and (min-width: 900px)': {
    gridTemplateColumns: '380px minmax(0, 1fr)',
    gap: '48px',
  },
});

const Header = styled('div', {
  padding: '20px 0',
  borderBottom: '1px solid #E9ECEF',
  marginBottom: '24px',
  backgroundColor: '#fff',
});

const Breadcrumb = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '16px',
  fontSize: '14px',
  color: '#666',
});

const BreadcrumbLink = styled('button', {
  background: 'none',
  border: 'none',
  color: '#666',
  textDecoration: 'underline',
  cursor: 'pointer',
  fontSize: '14px',
  padding: '0',
  ':hover': {
    color: '#000',
  },
});

const UserHeader = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '16px',
});

const UserInfo = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

const UserDetails = styled('div', {
  display: 'flex',
  flexDirection: 'column',
});

const UserName = styled('div', {
  fontSize: '24px',
  fontWeight: '600',
  color: '#000',
  marginBottom: '4px',
});

const UserRole = styled('div', {
  fontSize: '14px',
  color: '#666',
});

const TabContainer = styled('div', {
  display: 'flex',
  borderBottom: '1px solid #E9ECEF',
  marginBottom: '32px',
  overflowX: 'auto',
  '-webkit-overflow-scrolling': 'touch',
  backgroundColor: '#fff',
  // At 375 px the last tab runs off the edge ("A"); a soft shadow on the side that still has
  // tabs to scroll to makes it readable as a scrollable row (disappears once the end is reached).
  backgroundImage: [
    'linear-gradient(to right, #fff 30%, rgba(255,255,255,0))',
    'linear-gradient(to left, #fff 30%, rgba(255,255,255,0))',
    'radial-gradient(farthest-side at 0 50%, rgba(0,0,0,.18), rgba(0,0,0,0))',
    'radial-gradient(farthest-side at 100% 50%, rgba(0,0,0,.18), rgba(0,0,0,0))',
  ].join(', '),
  backgroundPosition: 'left center, right center, left center, right center',
  backgroundSize: '28px 100%, 28px 100%, 10px 100%, 10px 100%',
  backgroundRepeat: 'no-repeat',
  backgroundAttachment: 'local, local, scroll, scroll',
});

const Tab = styled('button', (props: { $active: boolean }) => ({
  padding: '12px 14px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontWeight: props.$active ? '600' : '400',
  color: props.$active ? '#000' : '#666',
  borderBottom: props.$active ? '2px solid #000' : '2px solid transparent',
  fontSize: '14px',
  transition: 'all 0.2s ease',
  whiteSpace: 'nowrap',
  ':hover': {
    color: '#000',
  },
  '@media screen and (min-width: 768px)': {
    padding: '12px 24px',
  },
}));

const TabContent = styled('div', {
  paddingBottom: '40px',
});

const IDCardContainer = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  padding: '24px',
  backgroundColor: '#F8F9FA',
  borderRadius: '12px',
  border: '1px solid #E9ECEF',
});

const IDCard = styled('div', {
  width: '400px',
  height: '250px',
  backgroundColor: '#fff',
  borderRadius: '12px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  overflow: 'hidden',
  border: '1px solid #DEE2E6',
  '@media screen and (max-width: 768px)': {
    width: '320px',
    height: '200px',
  },
  '@media screen and (max-width: 480px)': {
    width: '280px',
    height: '175px',
  },
});

const Section = styled('div', {
  marginBottom: '32px',
});

const SectionTitle = styled('div', {
  fontSize: '18px',
  fontWeight: '600',
  marginBottom: '24px',
  color: '#212529',
});

const FormGrid = styled('div', {
  display: 'grid',
  gap: '20px',
  gridTemplateColumns: '1fr',
  '@media screen and (min-width: 768px)': {
    gridTemplateColumns: '1fr 1fr',
  },
});

const FormField = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const FieldLabel = styled('label', {
  fontSize: '14px',
  fontWeight: '500',
  color: '#495057',
});

const ReadOnlyValue = styled('div', {
  padding: '12px 16px',
  backgroundColor: '#F8F9FA',
  borderRadius: '8px',
  fontSize: '14px',
  color: '#495057',
  border: '1px solid #E9ECEF',
});

const RolePermissionContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

const RoleRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 0',
  borderBottom: '1px solid #E9ECEF',
});

const RoleName = styled('div', (props: { $color?: string }) => ({
  fontSize: '14px',
  fontWeight: '500',
  color: props.$color || '#000',
}));

const RecordsTable = styled('div', {
  overflowX: 'auto',
  '-webkit-overflow-scrolling': 'touch',
});

const TableHeader = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: '16px',
  padding: '12px 0',
  borderBottom: '1px solid #E9ECEF',
  fontWeight: '600',
  fontSize: '14px',
  color: '#495057',
});

const TableRow = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: '16px',
  padding: '12px 0',
  borderBottom: '1px solid #E9ECEF',
  fontSize: '14px',
  color: '#495057',
});

const LoadingContainer = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '400px',
});

const BackButton = styled('button', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: '14px',
  color: '#666',
  padding: '8px 0',
  marginBottom: '16px',
  ':hover': {
    color: '#000',
  },
});

const BackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M6.5 3L2 7.5L6.5 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M2 7.5H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

type TabType = 'basic' | 'invitations' | 'estate' | 'app-user';

// ── User Invitations Tab ─────────────────────────────────────────────────
const InvSearchWrapper = styled('div', {
  display: 'flex', alignItems: 'center', gap: '8px', maxWidth: '280px',
  backgroundColor: '#EBEBEB', border: '1px solid #EBEBEB', borderRadius: '20px', padding: '7px 14px', marginBottom: '12px',
});
const InvSearchInput = styled('input', {
  border: 'none', outline: 'none', background: 'transparent', fontSize: '14px',
  color: '#374151', flex: 1, width: '100%', '::placeholder': { color: '#9CA3AF' },
});

const UserInvitationsTab: React.FC<{ userId: string }> = ({ userId }) => {
  // Dates here read as 7/4/2026 — ambiguous between July 4th and April 7th. The
  // i18n formatter writes the month out in the active language.
  const { fmt } = useT();
  const [invitations, setInvitations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvitation, setSelectedInvitation] = useState<any>(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    const fetchInvitations = async () => {
      try {
        const resp = await defaultInstance({
          method: 'GET',
          url: `/invitation?host_user_id=${userId}&expand=estate,estate_id,user,main_guest_user_id&order_by=create_date&order_direction=DESC&limit=500`,
        });
        const data = (resp as any)?.data?.data ?? (resp as any)?.data ?? [];
        setInvitations(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Error loading invitations:', e);
      } finally {
        setLoading(false);
      }
    };
    if (userId) fetchInvitations();
  }, [userId]);

  const filtered = useMemo(() => {
    if (!search.trim()) return invitations;
    const q = search.toLowerCase();
    return invitations.filter((inv: any) => {
      const mg = inv.user || inv.main_guest || {};
      return (
        (mg.name || '').toLowerCase().includes(q) ||
        (mg.last_name || '').toLowerCase().includes(q) ||
        (mg.tel || '').includes(q) ||
        (inv.estate_name || '').toLowerCase().includes(q) ||
        (inv.code || '').toLowerCase().includes(q) ||
        (inv.hik_pin || '').includes(q) ||
        String(inv.id || '').includes(q)
      );
    });
  }, [invitations, search]);

  const paged = useMemo(() => {
    const start = currentPage * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage]);

  const totalPages = Math.ceil(filtered.length / pageSize);

  useEffect(() => setCurrentPage(0), [search]);

  // Map invitation data to BusinessAccess-like format for ConfirmationView
  const mapToBusinessAccess = (inv: any) => ({
    ...inv,
    id: inv.code || inv.id,
    entity: 'invitation',
    main_guest: inv.user || inv.main_guest || {},
    business_access_type_id: 1,
    business_access_status_id: inv.status_id || 1,
    status_name: inv.status_id === 1 ? 'Pending' : inv.status_id === 2 ? 'Confirmed' : inv.status_id === 3 ? 'Checked In' : inv.status_id === 4 ? 'Checked Out' : inv.status_id === 5 ? 'Cancelled' : 'Unknown',
    start_date: inv.invitation_from,
    end_date: inv.invitation_to,
    create_date: inv.create_date,
    edit_date: inv.create_date,
    estate: inv.estate,
    estate_name: inv.estate_name || inv.estate?.name,
    legacy_code: inv.legacy_code || inv.code,
  });

  const invColumns = useMemo<MRT_ColumnDef<any>[]>(() => [
    {
      accessorKey: 'main_guest',
      header: 'Guest',
      size: 200,
      Cell: ({ row }) => {
        const mg = row.original?.user || row.original?.main_guest || {};
        const name = [mg.name, mg.last_name].filter(Boolean).join(' ') || '—';
        return (
          <div>
            <div style={{ fontWeight: 500, fontSize: '13px' }}>{name}</div>
            {(mg.tel || mg.email) && <div style={{ fontSize: '11px', color: '#9CA3AF' }}>{mg.tel || mg.email}</div>}
          </div>
        );
      },
    },
    {
      accessorKey: 'estate_name',
      header: 'Destination',
      size: 150,
      Cell: ({ row }) => <span style={{ fontSize: '13px' }}>{row.original?.estate_name || row.original?.estate?.name || '—'}</span>,
    },
    {
      accessorKey: 'status_id',
      header: 'Status',
      size: 100,
      Cell: ({ row }) => {
        const s = row.original?.status_id;
        const label = s === 1 ? 'Pending' : s === 2 ? 'Confirmed' : s === 3 ? 'Checked In' : s === 4 ? 'Checked Out' : s === 5 ? 'Cancelled' : 'Unknown';
        const color = s === 2 ? '#065F46' : s === 3 ? '#1E40AF' : s === 5 ? '#991B1B' : '#6B7280';
        const bg = s === 2 ? '#D1FAE5' : s === 3 ? '#DBEAFE' : s === 5 ? '#FEE2E2' : '#F3F4F6';
        return <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 500, backgroundColor: bg, color }}>{label}</span>;
      },
    },
    {
      accessorKey: 'invitation_from',
      header: 'From',
      size: 170,
      Cell: ({ cell }) => {
        const v = cell.getValue<string>();
        return v ? <span style={{ fontSize: '12px' }}>{fmt.dateTime(v)}</span> : '—';
      },
    },
    {
      accessorKey: 'invitation_to',
      header: 'To',
      size: 170,
      Cell: ({ cell }) => {
        const v = cell.getValue<string>();
        return v ? <span style={{ fontSize: '12px' }}>{fmt.dateTime(v)}</span> : '—';
      },
    },
    {
      accessorKey: 'hik_pin',
      header: 'PIN',
      size: 80,
      Cell: ({ cell }) => {
        const pin = cell.getValue<string>();
        return pin ? <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '13px' }}>{pin}</span> : '—';
      },
    },
  ], [fmt]);

  return (
    // SAM-577: MantineDataTable fills a height-bounded parent (its paper is
    // `height: 100%`) so the pagination bar stays pinned. This tab's ancestors
    // size to their content, so `flex: 1` had nothing to divide and the rows and
    // the footer were cut off by the layout's `overflow: hidden`. An explicit
    // height here bounds the table whatever the ancestors do.
    <Section style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: 'min(640px, calc(100vh - 360px))' }}>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><Spinner $size={24} /></div>
      ) : (
        <MantineDataTable
          columns={invColumns}
          data={invitations}
          enablePagination
          enableSorting
          initialPageSize={10}
          pageSizeOptions={[10, 20, 50]}
          containerHeight="100%"
          onRowClick={(row: any) => setSelectedInvitation(mapToBusinessAccess(row))}
        />
      )}

      {selectedInvitation && (
        <DrawerComponent
          isOpen={true}
          onClose={() => setSelectedInvitation(null)}
          anchor="right"
          overrides={{
            Root: { style: { zIndex: 10000 } },
            Backdrop: { style: { zIndex: 10000 } },
            DrawerContainer: { style: { width: '40vw', maxWidth: '500px', minWidth: '400px', zIndex: 10001 } },
            DrawerBody: { style: { padding: '0', height: '100%', display: 'flex', flexDirection: 'column' } },
          }}
        >
          <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
            <InvitationProvider>
              <ConfirmationView
                onClose={() => setSelectedInvitation(null)}
                invitationData={selectedInvitation}
                isViewMode={true}
              />
            </InvitationProvider>
          </div>
        </DrawerComponent>
      )}
    </Section>
  );
};

// ── Document Photo Section (used inside Basic Information) ────────────────
const DocumentPhotoSection = ({ userData, onDocumentUploaded }: { userData: any; onDocumentUploaded?: (result: any) => void }) => {
  const [showLightbox, setShowLightbox] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const photoKey = userData.photo_url || userData.document_url;

  const handleFile = async (file: File) => {
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64Data = (reader.result as string).split(',')[1];
      setIsUploading(true);
      setUploadStatus('loading');
      let didFail = false;
      try {
        const result = await analyzeDocument({ image: base64Data, filename: file.name });
        if (onDocumentUploaded) {
          onDocumentUploaded(result);
        }
      } catch (err) {
        console.error('Error analyzing uploaded document:', err);
        toaster.negative('Error analyzing document. Please try again.', { autoHideDuration: 4000 });
        didFail = true;
      } finally {
        setUploadStatus(didFail ? 'error' : 'success');
        window.setTimeout(() => {
          setIsUploading(false);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }, 1400);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  if (!photoKey) {
    return (
      <div style={{
        marginBottom: '28px',
        borderRadius: '12px',
        aspectRatio: '1.586 / 1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}>
        {isUploading ? (
          <DocumentScanner status={uploadStatus} size="md" />
        ) : (
          <UploadArea
            onFileSelect={handleFile}
            accept=".png,.jpg,.jpeg"
            title="Upload document photo"
            hint="Drag & drop or click to upload"
            buttonLabel="Upload"
            style={{ height: '100%' }}
          />
        )}
      </div>
    );
  }

  return (
    <>
      <div
        onDoubleClick={() => setShowLightbox(true)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          marginBottom: '28px',
          borderRadius: '12px',
          overflow: 'hidden',
          aspectRatio: '1.586 / 1',
          display: 'flex',
          justifyContent: 'center',
          backgroundColor: '#F3F4F6',
          cursor: 'zoom-in',
          position: 'relative',
        }}
      >
        <S3Image
          imageKey={photoKey}
          alt="Identity document"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', pointerEvents: 'none' }}
          showLoading={true}
        />
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: isHovered ? 1 : 0,
          transition: 'opacity 0.2s ease',
          pointerEvents: 'none',
        }}>
          <span style={{ color: '#fff', fontSize: '13px', fontWeight: 500, letterSpacing: '0.02em' }}>
            Double click to view
          </span>
        </div>
      </div>

      {/* Lightbox */}
      {showLightbox && ReactDOM.createPortal(
        <div
          onClick={() => setShowLightbox(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            zIndex: 99999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'zoom-out',
          }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setShowLightbox(false); }}
            style={{
              position: 'absolute', top: '20px', right: '20px',
              width: '40px', height: '40px', borderRadius: '50%', border: 'none',
              backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff',
              fontSize: '20px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backdropFilter: 'blur(4px)', transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.3)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'; }}
          >✕</button>
          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: '60vw', maxHeight: '70vh', cursor: 'default' }}>
            <S3Image
              imageKey={photoKey}
              alt="Identity document - Full size"
              style={{ maxWidth: '60vw', maxHeight: '70vh', objectFit: 'contain', borderRadius: '8px' }}
              showLoading={true}
            />
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

const getUserTypeName = (userTypeId: string | number): string => {
  const id = String(userTypeId);
  switch (id) {
    case '2':
      return 'Owner';
    case '3':
      return 'Resident';
    case '4':
      return 'Property Manager';
    default:
      // SAM-218: no resolvable global user type — return empty so the header
      // subtitle is hidden instead of showing a misleading "Unknown".
      return '';
  }
};

/** SAM-1323: where «Back» goes: the list the user was opened from (Users or Estate Requests), else the Users list. */
export const backTarget = (from?: string | null): string =>
  from && (from.startsWith('/users') || from.startsWith('/support/estate-requests')) ? from : '/users';
export const backLabel = (from?: string | null): string =>
  from && from.startsWith('/support/estate-requests') ? 'Estate Requests' : 'Users';

export const UserDetail = () => {
  const { fmt } = useT();
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const patchUserMutation = usePatchUserId();
  
  // Check if we should start on estate tab (from URL hash)
  const urlParams = new URLSearchParams(window.location.search);
  const tabFromUrl = urlParams.get('tab') as TabType;
  const [activeTab, setActiveTab] = useState<TabType>(tabFromUrl || 'basic');
  // The tenant Front Desk edits basic data, asks for phone changes and reads the history (specs/front-desk-tenant-permisos.md).
  const canEditBasics = useFdCan('users', 'edit', false);

  // Redirect if no userId provided
  useEffect(() => {
    if (!userId) {
      navigate('/users');
    }
  }, [userId, navigate]);

  // Fetch user data using the API
  const { 
    data: userResponse, 
    isLoading, 
    error 
  } = useGetUserId(
    userId!, 
    { 
      query: {
        enabled: !!userId,
        retry: 1,
      }
    }
  );

  // Estate actions are for the tenant admin (Cognito group in the token, rule 14); the API re-checks with a 403.
  const { isAdmin } = useUserPermissions();

  // Handle API errors
  useEffect(() => {
    if (error as any) {
      console.error('Error loading user data:', error);
      toaster.negative('Error loading user data', { autoHideDuration: 3000 });
    }
  }, [error]);

  const userData = userResponse?.data as any;

  // Profile picture state
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const profilePicGenRef = useRef(false);

  // "Add estate" drawer for the Estate tab
  const [addEstateOpen, setAddEstateOpen] = useState(false);

  // Sync profile_picture from server data
  useEffect(() => {
    if (userData?.profile_picture) {
      setProfilePicture(userData.profile_picture);
    }
  }, [userData?.profile_picture]);

  // Auto-generate profile picture if user has photo but no profile_picture
  useEffect(() => {
    const photoUrl = userData?.photo_url || userData?.document_url;
    if (
      userData &&
      !userData.profile_picture &&
      !profilePicture &&
      photoUrl &&
      !profilePicGenRef.current
    ) {
      profilePicGenRef.current = true;
      (async () => {
        try {
          const result = await generateProfilePicture(photoUrl);
          if (result) {
            setProfilePicture(result);
            // Persist to the user record
            await patchUserMutation.mutateAsync({
              id: String(userData.id),
              data: { profile_picture: result } as any,
            });
            queryClient.invalidateQueries({ queryKey: ['/user'], exact: false });
          }
        } catch (err) {
          console.error('Failed to generate profile picture:', err);
        }
      })();
    }
  }, [userData]);

  // Auto re-analyze document if user has a photo but missing key fields
  const reAnalyzeRef = useRef(false);
  useEffect(() => {
    const photoUrl = userData?.photo_url || userData?.document_url;
    if (
      userData &&
      photoUrl &&
      !reAnalyzeRef.current &&
      (!userData.idnum || !userData.birthdate || !userData.nationality)
    ) {
      reAnalyzeRef.current = true;
      (async () => {
        try {
          console.log('[ReAnalyze] Triggering re-analysis for missing fields...');
          const result = await reAnalyzeFromS3(photoUrl);
          if (result?.data) {
            const updates: Record<string, any> = {};
            const d = result.data;
            if (!userData.idnum && d.number) updates.idnum = d.number;
            if (!userData.birthdate && d.DOB) updates.birthdate = d.DOB;
            if (!userData.nationality && d.nationality) updates.nationality = d.nationality;
            if (!userData.name && d.name) updates.name = d.name;
            if (!userData.last_name && d.LastName) updates.last_name = d.LastName;

            if (Object.keys(updates).length > 0) {
              console.log('[ReAnalyze] Updating user with:', updates);
              await patchUserMutation.mutateAsync({
                id: String(userData.id),
                data: updates as any,
              });
              queryClient.invalidateQueries({ queryKey: ['/user'], exact: false });
            } else {
              console.log('[ReAnalyze] No new fields extracted from OCR.');
            }
          }
        } catch (err) {
          console.error('[ReAnalyze] Failed to re-analyze document:', err);
        }
      })();
    }
  }, [userData]);

  const [isRegenerating, setIsRegenerating] = useState(false);

  const handleRegenerateProfilePicture = async () => {
    const photoUrl = userData?.photo_url || userData?.document_url;
    if (!photoUrl || isRegenerating) return;
    setIsRegenerating(true);
    try {
      const result = await generateProfilePicture(photoUrl);
      if (result) {
        setProfilePicture(result);
        await patchUserMutation.mutateAsync({
          id: String(userData.id),
          data: { profile_picture: result } as any,
        });
        queryClient.invalidateQueries({ queryKey: ['/user'], exact: false });
      }
    } catch (err) {
      console.error('Failed to regenerate profile picture:', err);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleBack = () => {
    // The Users list passes its URL (search + tab) in location.state so the
    // filtered view is restored; fall back to the plain list otherwise.
    const from = (location.state as { from?: string } | null)?.from;
    navigate(backTarget(from));
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!userData || !userData.id) {
      throw new Error('User ID not available');
    }

    try {
      await patchUserMutation.mutateAsync({
        id: String(userData.id),
        data: {
          owner_resident_status: newStatus
        } as any
      });

      // Invalidate user queries to refresh data
      queryClient.invalidateQueries({ 
        queryKey: ['/user'],
        exact: false
      });
      
      queryClient.invalidateQueries({ 
        queryKey: [`/user/${String(userData.id)}`]
      });
      
      queryClient.refetchQueries({ 
        queryKey: [`/user/${String(userId)}`]
      });

      toaster.positive(`User status updated to ${newStatus}`, {
        autoHideDuration: 3000,
      });

    } catch (error) {
      console.error('Error updating user status:', error);
      toaster.negative('Error updating user status', {
        autoHideDuration: 3000,
      });
      throw error;
    }
  };

  if (isLoading) {
    return (
      <Content>
        <Container>
          <LoadingContainer>
            <Spinner $size={40} />
          </LoadingContainer>
        </Container>
      </Content>
    );
  }

  if (!userData) {
    return (
      <Content>
        <Container>
          <div>User not found</div>
        </Container>
      </Content>
    );
  }

  const fullName = `${userData.name || ''} ${userData.last_name || ''}`.trim();

  return (
    <div style={{ backgroundColor: '#fff', height: '100%', overflowY: 'auto' }}>
      <Container>
        <BackButton onClick={handleBack}>
          <BackIcon />
          Back to {backLabel((location.state as { from?: string } | null)?.from)}
        </BackButton>

        <Header>
          <Breadcrumb>
            <BreadcrumbLink onClick={handleBack}>{backLabel((location.state as { from?: string } | null)?.from)}</BreadcrumbLink>
            <span>›</span>
            <span>{fullName}</span>
          </Breadcrumb>

          <UserHeader>
            <UserInfo>
              {profilePicture ? (
                <div
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    flexShrink: 0,
                    backgroundColor: '#F3F4F6',
                    border: '2px solid #E5E7EB',
                    position: 'relative',
                    cursor: 'pointer',
                  }}
                  className="profile-pic-container"
                  onClick={handleRegenerateProfilePicture}
                  title="Click to regenerate profile picture"
                >
                  <S3Image
                    imageKey={profilePicture}
                    alt={fullName}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    showLoading={true}
                  />
                  <div
                    className="profile-pic-overlay"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0,
                      transition: 'opacity 0.2s ease',
                    }}
                  >
                    {isRegenerating ? (
                      <Spinner $size={20} $borderWidth={2} />
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="23 4 23 10 17 10" />
                        <polyline points="1 20 1 14 7 14" />
                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                      </svg>
                    )}
                  </div>
                  <style>{`
                    .profile-pic-container:hover .profile-pic-overlay {
                      opacity: 1 !important;
                    }
                  `}</style>
                </div>
              ) : (
                  <Avatar name={fullName} size="64px" />
              )}
              <UserDetails>
                <UserName>{fullName}</UserName>
                {getUserTypeName(userData.user_type || '') && (
                  <UserRole>{getUserTypeName(userData.user_type || '')}</UserRole>
                )}
              </UserDetails>
            </UserInfo>
          </UserHeader>
        </Header>

        <TabContainer>
          <Tab $active={activeTab === 'basic'} onClick={() => setActiveTab('basic')}>
            Basic information
          </Tab>
          {/* SAM-38: Roles tab temporarily hidden */}
          {/* <Tab $active={activeTab === 'roles'} onClick={() => setActiveTab('roles')}>
            Roles and permissions
          </Tab> */}
          <Tab $active={activeTab === 'invitations'} onClick={() => setActiveTab('invitations')}>
            Passes
          </Tab>
          <Tab $active={activeTab === 'estate'} onClick={() => setActiveTab('estate')}>
            Estate
          </Tab>
          <Tab $active={activeTab === 'app-user'} onClick={() => setActiveTab('app-user')}>
            App User Info
          </Tab>
        </TabContainer>

        <TabContent>
          {activeTab === 'basic' && (
            <BasicGrid>
              {/* Left: Identity Document */}
              <div>
                <div style={{ fontSize: '13px', fontWeight: 500, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>
                  Identity Document
                </div>
                <DocumentPhotoSection userData={userData} onDocumentUploaded={async (result) => {
                  try {
                    const updates: Record<string, any> = {};
                    const d = result?.data;
                    if (d?.picture) updates.photo_url = d.picture;
                    if (d?.number) updates.idnum = d.number;
                    if (d?.DOB) updates.birthdate = d.DOB;
                    if (d?.nationality) updates.nationality = d.nationality;
                    if (d?.name) updates.name = d.name;
                    if (d?.LastName) updates.last_name = d.LastName;

                    if (Object.keys(updates).length > 0) {
                      await patchUserMutation.mutateAsync({
                        id: String(userData.id),
                        data: updates as any,
                      });
                      queryClient.invalidateQueries({ queryKey: ['/user'], exact: false });
                      toaster.positive('Document analyzed and user data updated', { autoHideDuration: 3000 });
                    } else {
                      toaster.warning('No data could be extracted from the document', { autoHideDuration: 3000 });
                    }
                  } catch (err) {
                    console.error('Error updating user after document upload:', err);
                    toaster.negative('Error updating user data', { autoHideDuration: 3000 });
                  }
                }} />
              </div>

              {/* Right: Personal Information */}
              <div>
                <div style={{ fontSize: '13px', fontWeight: 500, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>
                  Personal Information
                </div>

                {(() => {
                  type Row =
                    | { label: string; value: any; kind?: 'text' }
                    | { label: 'Nationality'; kind: 'nationality' };
                  const rows: Row[] = [
                    { label: 'First Name', value: userData.name },
                    { label: 'Last Name', value: userData.last_name },
                    { label: 'ID Number', value: (userData as any).idnum },
                    { label: 'Nationality', kind: 'nationality' },
                    { label: 'Date of Birth', value: (userData as any).birthdate ? fmt.date((userData as any).birthdate) : null },
                    { label: 'Phone', value: userData.phone || userData.tel },
                    { label: 'Email', value: userData.email },
                  ];
                  return rows.map((row, i, arr) => (
                    <div
                      key={row.label}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '13px 0',
                        borderBottom: i < arr.length - 1 ? '1px solid #F3F4F6' : 'none',
                      }}
                    >
                      <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: 400 }}>{row.label}</span>
                      {row.kind === 'nationality' ? (
                        <div style={{ minWidth: '60%', maxWidth: '60%' }}>
                          <NationalityPicker
                            value={userData.nationality}
                            size="compact"
                            onChange={async (code) => {
                              try {
                                await patchUserMutation.mutateAsync({
                                  id: String(userData.id),
                                  data: { nationality: code } as any,
                                });
                                queryClient.invalidateQueries({ queryKey: ['/user'], exact: false });
                                toaster.positive('Nationality updated', { autoHideDuration: 2000 });
                              } catch (err) {
                                console.error('Error updating nationality:', err);
                                toaster.negative('Failed to update nationality', { autoHideDuration: 3000 });
                              }
                            }}
                          />
                        </div>
                      ) : (
                        <span style={{
                          fontSize: '14px',
                          color: row.value ? '#111827' : '#D1D5DB',
                          fontWeight: row.value ? 500 : 400,
                          textAlign: 'right',
                          maxWidth: '60%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {row.value || '—'}
                        </span>
                      )}
                    </div>
                  ));
                })()}

                {/* SAM-629: verification state + reception's mark / clear control */}
                <div style={{ marginTop: '24px' }}>
                  <VerifiedDocumentCard
                    userId={userData.id}
                    cognitoId={userData.cognitoid}
                    value={userData.verified_document}
                    defaultDocument={userData.idtype}
                    defaultNumber={userData.idnum}
                    onChanged={(verified_document) => {
                      queryClient.setQueryData(getGetUserIdQueryKey(String(userId)), (old: any) =>
                        old?.data ? { ...old, data: { ...old.data, verified_document } } : old);
                    }}
                  />
                </div>
              </div>
            </BasicGrid>
          )}

          {activeTab === 'basic' && canEditBasics && userData && (
            <FrontDeskUserBasics
              key={String(userData.id ?? userId)}
              user={userData}
              onChanged={() => queryClient.invalidateQueries({ queryKey: getGetUserIdQueryKey(String(userId)) })}
            />
          )}

          {/* SAM-38: Roles and permissions section temporarily hidden */}
          {/* activeTab === 'roles' && (
            <>
              <Section>
                <SectionTitle>User Status Management</SectionTitle>
                <div style={{ marginBottom: '20px' }}>
                  <UserStatusSelector
                    currentStatus={userData.owner_resident_status || 'processing'}
                    userId={String(userData.id)}
                    onStatusChange={handleStatusChange}
                    onStatusUpdated={() => {
                    }}
                    disabled={patchUserMutation.isPending}
                  />
                </div>
              </Section>

              <Section>
                <SectionTitle>Roles and permissions</SectionTitle>
                <RolePermissionContainer>
                  <TableHeader>
                    <div>Role</div>
                    <div>Permission</div>
                  </TableHeader>
                  
                  <RoleRow>
                    <RoleName>Business</RoleName>
                    <Checkbox checked={false} disabled />
                  </RoleRow>
                  
                  <RoleRow>
                    <RoleName $color="#D97706">Admin</RoleName>
                    <Checkbox checked={false} disabled />
                  </RoleRow>
                  
                  <RoleRow>
                    <RoleName>Employee</RoleName>
                    <Checkbox checked={true} disabled />
                  </RoleRow>
                  
                  <RoleRow>
                    <RoleName $color="#3B82F6">Owner</RoleName>
                    <Checkbox checked={false} disabled />
                  </RoleRow>
                </RolePermissionContainer>
              </Section>
            </>
          ) */}

          {activeTab === 'invitations' && (
            <UserInvitationsTab userId={userId} />
          )}

          {activeTab === 'app-user' && (
            <AppUserInfoTab
              cognitoId={userData?.cognitoid}
              email={userData?.email}
              userId={userData?.id}
              onOpenUser={(id) => navigate(`/users/${id}`)}
            />
          )}

          {activeTab === 'estate' && (
            <UserEstateTab userId={Number(userId)} canEdit={isAdmin} onAddEstate={() => setAddEstateOpen(true)} />
          )}
        </TabContent>
      </Container>

      {userId && (
        <AddUserEstateDrawer
          isOpen={addEstateOpen}
          userId={Number(userId)}
          onClose={() => setAddEstateOpen(false)}
        />
      )}
    </div>
  );
}; 